import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import SignatureCanvas from 'react-signature-canvas';
import {
  FileText,
  Upload,
  Check,
  ChevronRight,
  ChevronLeft,
  PenLine,
  CreditCard,
  FileCheck,
  ClipboardCheck,
  Loader2,
  Trash2,
  Shield,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'contractor_agreement', label: 'Contractor Agreement', icon: FileText, desc: 'Independent Contractor Agreement & Annexure A' },
  { key: 'direct_deposit', label: 'Direct Deposit', icon: CreditCard, desc: 'ACH Authorization Form' },
  { key: 'referral_agreement', label: 'Referral Fee Agreement', icon: FileCheck, desc: 'Annexure B - Referral Fee Policy' },
  { key: 'w9', label: 'W-9 Form', icon: ClipboardCheck, desc: 'IRS Tax Identification' },
  { key: 'review', label: 'Review & Submit', icon: Check, desc: 'Review and finalize' }
];

// ============ SIGNATURE PAD COMPONENT ============
const SignaturePad = ({ onSave, label }) => {
  const sigRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const clear = () => { sigRef.current?.clear(); setIsEmpty(true); };
  const save = () => {
    if (sigRef.current?.isEmpty()) { toast.error('Please sign first'); return; }
    onSave(sigRef.current.toDataURL('image/png'));
  };

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2"><PenLine className="w-4 h-4" />{label || 'E-Signature'}</Label>
      <div className="border-2 border-dashed border-slate-200 rounded-xl bg-white overflow-hidden">
        <SignatureCanvas
          ref={sigRef}
          penColor="#1B365D"
          canvasProps={{ className: 'w-full h-32', style: { width: '100%', height: '128px' } }}
          onBegin={() => setIsEmpty(false)}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={clear} className="rounded-full"><Trash2 className="w-3 h-3 mr-1" />Clear</Button>
        <Button size="sm" onClick={save} disabled={isEmpty} className="rounded-full bg-[#1B365D] hover:bg-[#1B365D]/90" data-testid="sign-btn"><PenLine className="w-3 h-3 mr-1" />Sign & Accept</Button>
      </div>
    </div>
  );
};

// ============ FILE UPLOAD COMPONENT ============
const FileUploadOption = ({ step, token, onComplete }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/onboarding/upload/${step}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      onComplete();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
      <p className="text-sm font-medium text-slate-700 mb-2">Or upload a completed document</p>
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={handleUpload} />
      <Button variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`upload-${step}`}>
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {uploading ? 'Uploading...' : 'Choose File (PDF/Image)'}
      </Button>
    </div>
  );
};

// ============ STEP: CONTRACTOR AGREEMENT ============
const ContractorAgreementStep = ({ token, onComplete, onSkip, stepData }) => {
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/templates/contractor_agreement`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTemplate(r.data)).catch(() => {});
  }, [token]);

  if (stepData?.status === 'completed') return <StepCompleted label="Contractor Agreement" signedAt={stepData.signed_at || stepData.uploaded_at} />;

  return (
    <div className="space-y-5" data-testid="step-contractor-agreement">
      <div>
        <h2 className="text-xl font-semibold text-[#1B365D]">{template?.title || 'Independent Contractor Agreement'}</h2>
        <p className="text-sm text-slate-500 mt-1">Review the agreement terms, then sign below or upload a signed copy.</p>
      </div>
      <Card className="border-0 shadow-sm">
        <ScrollArea className="h-64 p-5">
          <div className="prose prose-sm prose-slate max-w-none text-xs leading-relaxed space-y-4">
            {template?.sections?.map((s, i) => (
              <div key={i}>
                <h4 className="text-xs font-bold mt-3">{s.heading}</h4>
                <p className="whitespace-pre-line">{s.body}</p>
              </div>
            )) || <p className="text-slate-400">Loading agreement terms...</p>}
          </div>
        </ScrollArea>
      </Card>
      <a href="https://customer-assets.emergentagent.com/job_18c5d0c1-39eb-46ca-9d55-9c0ffabaa63c/artifacts/8br27nxk_Independent%20Contractor%20Onboarding.pdf" target="_blank" rel="noopener noreferrer" className="text-sm text-[#0071E3] hover:underline inline-block">View full agreement PDF</a>
      <SignaturePad label="Sign to accept the Contractor Agreement & Annexure A" onSave={async (sig) => {
        try {
          await axios.post(`${API_URL}/api/onboarding/sign/contractor_agreement`, { signature: sig }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Agreement signed'); onComplete();
        } catch { toast.error('Failed to sign'); }
      }} />
      <FileUploadOption step="contractor_agreement" token={token} onComplete={onComplete} />
      <button onClick={onSkip} className="text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="skip-step">Save for later</button>
    </div>
  );
};

// ============ STEP: DIRECT DEPOSIT ============
const DirectDepositStep = ({ token, onComplete, onSkip, stepData }) => {
  if (stepData?.status === 'completed') return <StepCompleted label="Direct Deposit Authorization" signedAt={stepData.signed_at || stepData.uploaded_at} />;

  return (
    <div className="space-y-5" data-testid="step-direct-deposit">
      <div>
        <h2 className="text-xl font-semibold text-[#1B365D]">Direct Deposit / ACH Authorization</h2>
        <p className="text-sm text-slate-500 mt-1">Download the ACH authorization form, complete it offline, and upload the signed document.</p>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Sensitive Information Protection</p>
              <p className="text-xs text-amber-700 mt-1">For your security, bank account details and SSN are not collected through this portal. Please download the form below, fill it out offline, and upload the completed document.</p>
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700">Instructions:</p>
            <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside">
              <li>Download the ACH Direct Deposit Authorization Form</li>
              <li>Fill in your banking details (name, bank, routing number, account number)</li>
              <li>Sign the form and attach a voided check (if applicable)</li>
              <li>Upload the completed form using the upload button below</li>
            </ol>
          </div>
          <a
            href="https://customer-assets.emergentagent.com/job_18c5d0c1-39eb-46ca-9d55-9c0ffabaa63c/artifacts/8br27nxk_Independent%20Contractor%20Onboarding.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1B365D] text-white text-sm font-medium hover:bg-[#1B365D]/90 transition-colors"
            data-testid="dd-download-form"
          >
            <FileText className="w-4 h-4" />
            Download ACH Authorization Form
          </a>
        </CardContent>
      </Card>
      <div>
        <p className="text-xs text-slate-600 font-medium mb-2">Upload completed ACH form:</p>
        <FileUploadOption step="direct_deposit" token={token} onComplete={onComplete} />
      </div>
      <button onClick={onSkip} className="text-xs text-slate-400 hover:text-slate-600" data-testid="skip-step">Save for later</button>
    </div>
  );
};

// ============ STEP: REFERRAL FEE AGREEMENT ============
const ReferralAgreementStep = ({ token, onComplete, onSkip, stepData }) => {
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/templates/referral_agreement`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTemplate(r.data)).catch(() => {});
  }, [token]);

  if (stepData?.status === 'completed') return <StepCompleted label="Referral Fee Agreement" signedAt={stepData.signed_at || stepData.uploaded_at} />;

  return (
    <div className="space-y-5" data-testid="step-referral-agreement">
      <div>
        <h2 className="text-xl font-semibold text-[#1B365D]">{template?.title || 'Referral Fee Agreement'}</h2>
        <p className="text-sm text-slate-500 mt-1">Review the referral fee policy (Annexure B), then sign or upload.</p>
      </div>
      <Card className="border-0 shadow-sm">
        <ScrollArea className="h-64 p-5">
          <div className="prose prose-sm prose-slate max-w-none text-xs leading-relaxed space-y-4">
            {template?.sections?.map((s, i) => (
              <div key={i}>
                <h4 className="text-xs font-bold">{s.heading}</h4>
                <p className="whitespace-pre-line">{s.body}</p>
              </div>
            )) || <p className="text-slate-400">Loading agreement terms...</p>}
          </div>
        </ScrollArea>
      </Card>
      <a href="https://customer-assets.emergentagent.com/job_18c5d0c1-39eb-46ca-9d55-9c0ffabaa63c/artifacts/8br27nxk_Independent%20Contractor%20Onboarding.pdf" target="_blank" rel="noopener noreferrer" className="text-sm text-[#0071E3] hover:underline inline-block">View full agreement PDF</a>
      <SignaturePad label="Sign to accept the Referral Fee Agreement" onSave={async (sig) => {
        try {
          await axios.post(`${API_URL}/api/onboarding/sign/referral_agreement`, { signature: sig }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Referral agreement signed'); onComplete();
        } catch { toast.error('Failed to sign'); }
      }} />
      <FileUploadOption step="referral_agreement" token={token} onComplete={onComplete} />
      <button onClick={onSkip} className="text-xs text-slate-400 hover:text-slate-600" data-testid="skip-step">Save for later</button>
    </div>
  );
};

// ============ STEP: W-9 ============
const W9Step = ({ token, onComplete, onSkip, stepData }) => {
  if (stepData?.status === 'completed') return <StepCompleted label="W-9 Form" signedAt={stepData.signed_at || stepData.uploaded_at} />;

  return (
    <div className="space-y-5" data-testid="step-w9">
      <div>
        <h2 className="text-xl font-semibold text-[#1B365D]">W-9 Form</h2>
        <p className="text-sm text-slate-500 mt-1">Download the official IRS W-9 form, complete it offline, and upload the signed document.</p>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Sensitive Information Protection</p>
              <p className="text-xs text-amber-700 mt-1">For your security, taxpayer identification numbers (SSN/EIN) and personal tax information are not collected through this portal. Please download the official IRS W-9 form, fill it out offline, and upload the completed document.</p>
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700">Instructions:</p>
            <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside">
              <li>Download the official IRS W-9 form (Request for Taxpayer Identification Number)</li>
              <li>Fill in your legal name, business name, tax classification, address, and TIN (SSN or EIN)</li>
              <li>Sign and date the form</li>
              <li>Upload the completed W-9 using the upload button below</li>
            </ol>
          </div>
          <a
            href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1B365D] text-white text-sm font-medium hover:bg-[#1B365D]/90 transition-colors"
            data-testid="w9-download-form"
          >
            <FileText className="w-4 h-4" />
            Download IRS W-9 Form (PDF)
          </a>
        </CardContent>
      </Card>
      <div>
        <p className="text-xs text-slate-600 font-medium mb-2">Upload completed W-9 form:</p>
        <FileUploadOption step="w9" token={token} onComplete={onComplete} />
      </div>
      <button onClick={onSkip} className="text-xs text-slate-400 hover:text-slate-600" data-testid="skip-step">Save for later</button>
    </div>
  );
};

// ============ STEP: REVIEW ============
const ReviewStep = ({ steps, onComplete, loading }) => {
  const completedCount = STEPS.filter(s => s.key !== 'review' && steps[s.key]?.status === 'completed').length;
  const totalRequired = STEPS.length - 1;

  return (
    <div className="space-y-5" data-testid="step-review">
      <div>
        <h2 className="text-xl font-semibold text-[#1B365D]">Review & Submit</h2>
        <p className="text-sm text-slate-500 mt-1">Review your completed documents before finalizing.</p>
      </div>
      <div className="space-y-3">
        {STEPS.filter(s => s.key !== 'review').map(s => {
          const Icon = s.icon;
          const status = steps[s.key]?.status || 'pending';
          const isComplete = status === 'completed';
          const isUploaded = steps[s.key]?.uploaded_file;
          return (
            <div key={s.key} className={`flex items-center gap-4 p-4 rounded-xl border ${isComplete ? 'bg-green-50/50 border-green-200' : status === 'skipped' ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-100' : 'bg-slate-100'}`}>
                {isComplete ? <Check className="w-5 h-5 text-green-600" /> : <Icon className="w-5 h-5 text-slate-400" />}
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{s.label}</p>
                <p className="text-xs text-slate-500">
                  {isComplete ? (isUploaded ? 'Document uploaded' : 'Signed electronically') : status === 'skipped' ? 'Skipped - complete before submitting' : 'Not started'}
                </p>
              </div>
              <Badge variant={isComplete ? 'default' : 'secondary'} className={isComplete ? 'bg-green-600' : ''}>{isComplete ? 'Complete' : status === 'skipped' ? 'Skipped' : 'Pending'}</Badge>
            </div>
          );
        })}
      </div>
      {completedCount < totalRequired && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <p className="font-medium">Some steps are incomplete</p>
          <p className="text-xs mt-1">You can still submit, but please complete all forms as soon as possible. Your admin will be notified.</p>
        </div>
      )}
      <Button onClick={onComplete} disabled={loading} className="w-full bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full h-12 text-base" data-testid="complete-onboarding-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Check className="w-5 h-5 mr-2" />}
        {completedCount >= totalRequired ? 'Complete Onboarding' : 'Submit & Complete Later'}
      </Button>
    </div>
  );
};

// ============ COMPLETED STEP BADGE ============
const StepCompleted = ({ label, signedAt }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="step-completed">
    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4"><Check className="w-8 h-8 text-green-600" /></div>
    <h3 className="text-lg font-semibold text-green-800">{label} Complete</h3>
    {signedAt && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(signedAt).toLocaleString()}</p>}
    <p className="text-sm text-slate-500 mt-2">Click "Next" to continue.</p>
  </div>
);

// ============ MAIN ONBOARDING PAGE ============
const Onboarding = ({ onComplete: onOnboardingDone, isResume }) => {
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
      setOnboardingData(res.data);
      if (res.data.completed) { onOnboardingDone?.(); return; }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token, onOnboardingDone]);

  useEffect(() => {
    fetchStatus();
    // Check if resuming at a specific step from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const resumeStep = urlParams.get('step');
    if (resumeStep) {
      const stepIdx = parseInt(resumeStep, 10);
      if (!isNaN(stepIdx) && stepIdx >= 0 && stepIdx < STEPS.length) {
        setCurrentStep(stepIdx);
      }
    }
  }, [fetchStatus]);

  const handleStepComplete = () => { fetchStatus(); };
  const handleSkip = async (step) => {
    try {
      await axios.post(`${API_URL}/api/onboarding/skip/${step}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentStep(s => Math.min(s + 1, STEPS.length - 1));
      fetchStatus();
    } catch { toast.error('Failed to skip'); }
  };

  const handleSaveAndExit = async () => {
    try {
      await axios.post(`${API_URL}/api/onboarding/save-for-later`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Progress saved! You can resume from Onboarding Documents anytime.');
      onOnboardingDone?.();
    } catch {
      toast.error('Failed to save progress');
    }
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/onboarding/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Onboarding complete! Welcome aboard.');
      onOnboardingDone?.();
    } catch { toast.error('Failed to complete'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1B365D]" /></div>;

  const steps = onboardingData?.steps || {};
  const stepConfig = STEPS[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#1B365D]">Welcome to Enable Healthcare</h1>
          <p className="text-slate-500 mt-1">Complete your onboarding to get started</p>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center gap-1 mb-8" data-testid="onboarding-progress">
          {STEPS.map((s, i) => {
            const isComplete = steps[s.key]?.status === 'completed';
            const isCurrent = i === currentStep;
            return (
              <button key={s.key} onClick={() => setCurrentStep(i)}
                className={`flex-1 h-2 rounded-full transition-all ${isComplete ? 'bg-green-500' : isCurrent ? 'bg-[#0071E3]' : 'bg-slate-200'}`}
                data-testid={`progress-${s.key}`}
              />
            );
          })}
        </div>

        {/* Step Labels */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{currentStep + 1} / {STEPS.length}</Badge>
            <span className="text-sm font-medium text-slate-800">{stepConfig.label}</span>
          </div>
          <span className="text-xs text-slate-400">{stepConfig.desc}</span>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            {stepConfig.key === 'contractor_agreement' && <ContractorAgreementStep token={token} onComplete={handleStepComplete} onSkip={() => handleSkip('contractor_agreement')} stepData={steps.contractor_agreement} />}
            {stepConfig.key === 'direct_deposit' && <DirectDepositStep token={token} onComplete={handleStepComplete} onSkip={() => handleSkip('direct_deposit')} stepData={steps.direct_deposit} />}
            {stepConfig.key === 'referral_agreement' && <ReferralAgreementStep token={token} onComplete={handleStepComplete} onSkip={() => handleSkip('referral_agreement')} stepData={steps.referral_agreement} />}
            {stepConfig.key === 'w9' && <W9Step token={token} onComplete={handleStepComplete} onSkip={() => handleSkip('w9')} stepData={steps.w9} />}
            {stepConfig.key === 'review' && <ReviewStep steps={steps} onComplete={handleFinalSubmit} loading={submitting} />}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        {stepConfig.key !== 'review' && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setCurrentStep(s => Math.max(0, s - 1))} disabled={currentStep === 0} className="rounded-full" data-testid="prev-step">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button variant="ghost" onClick={handleSaveAndExit} className="rounded-full text-slate-500" data-testid="save-exit-btn">
                <Clock className="w-4 h-4 mr-1" /> Save & Exit
              </Button>
            </div>
            <Button onClick={() => setCurrentStep(s => Math.min(s + 1, STEPS.length - 1))} className="rounded-full bg-[#0071E3] hover:bg-[#0071E3]/90" data-testid="next-step">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
